import { Enum_Survey_Access_Restriction, notEmpty } from '@cyren/common-lib';
import classNames from 'classnames';
import { memo, useState, useEffect } from 'react';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import Footer from '../../app/Footer';
import thumbsUp from '../../assets/ill/Thumbs_up.svg';
import LoadingAnim from '../../components/LoadingAnim';
import LogoPatientDynamic from '../../components/LogoPatientDynamic';
import PatientOrgContainer from '../hooks/PatientOrgContainer';
import { useData } from '../../report/use-data';
import { usePatientTransData } from '../../report/use-patient-trans-data';
import { useSurveyMeta } from '../../survey/use-survey-meta';
import { useTranslations } from '../hooks/use-translations';
import PaTreeHeader from '../tree/PaTreeHeader';
import { Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useRecoilState } from 'recoil';
import { PaReportState } from '../hooks/use-report';
import { TOptions } from '../pa-prop-types';
import { useSurveyState } from '../hooks/use-survey-state';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';

function PaSurveyTerminatedPageContent() {
  const [
    {
      surveyId,
      dataState: { survey, visit },
    },
  ] = useData({ disableRedirectOnInvalidReport: true });

  const nav = useNavigate();
  const { sltStr } = useStaticLocales();
  const [, { t }] = useTranslations();
  const container = PatientOrgContainer.useContainer();
  const location = useLocation();

  const [allowNavigate, setAllowNavigate] = useState(false);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  
  // 종료 사유 상태 (기본값: 완료)
  const [terminationReason, setTerminationReason] = useState<'completed' | 'timeout' | 'with_staff'>('completed');

  // reportState, surveyState
  const [reportState] = useRecoilState(PaReportState);
  const [{ surveyState }] = useSurveyState();

  // surveyState의 encounterCheckReason 확인하여 종료 사유 설정
  useEffect(() => {
    if (surveyState?.encounterCheckReason === 'timeout') {
      setTerminationReason('timeout');
    } else if (surveyState?.encounterCheckReason === 'with_staff') {
      setTerminationReason('with_staff');
    } else if (visit?.status === 'done' || visit?.archived) {
      setTerminationReason('completed');
    }
  }, [surveyState?.encounterCheckReason, visit?.status, visit?.archived]);

  const { transKeys, metaItems } = useSurveyMeta({
    surveyId,
    metaKey: 'faq',
  });

  const tOptions = (reportState?.systemValueMap ? Object.fromEntries(
    Object.entries(reportState?.systemValueMap).filter(([ , value]) => typeof value === 'string'))
    : {}) as TOptions;
  const titleLabel = t('finish-page-title', tOptions, { fallbackValue: null });
  const subtitleLabel = t('finish-page-desc', tOptions, { fallbackValue: null });

  const pageKeys = survey?.metadata?.finishedPageKeys || [];
  const [{ loadingTrans }] = usePatientTransData({
    keys: [...pageKeys, ...transKeys],
  });

  if (!survey || loadingTrans) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  // 종료 사유에 따른 메시지 렌더링
  const renderMessage = () => {
    if (terminationReason === 'timeout') {
      return (
        <div className='flex-center flex-col gap-4'>
          <div className='pt-12 pb-6 w-1/3'>
            {/* 타임아웃 아이콘이 있다면 변경 가능 */}
            <img src={thumbsUp} />
          </div>
          <div className='flex-col gap-2 flex-center-y'>
            <h4 className='font-bold sm'>Session Expired</h4>
            <div className='text-base opacity-70 px-2 text-center'>
            You&apos;ve reached the 30-minute limit. The information you have submitted so far will be sent to your documents. If you weren&apos;t able to enter details of your symptoms, no worries. Your care team will guide you from here.
            </div>
          </div>
        </div>
      );
    }

    if (terminationReason === 'with_staff') {
      return (
        <div className='flex-center flex-col gap-4'>
          <div className='pt-12 pb-6 w-1/3'>
            <img src={thumbsUp} />
          </div>
          <div className='flex-col gap-2 flex-center-y'>
            <h4 className='font-bold sm'>Visit Started</h4>
            <div className='text-base opacity-70 px-2 text-center'>
              Your information has been sent to your documents because your care team is ready to see you. If you weren&apos;t able to enter details of your symptoms, no worries. Your care team will guide you from here.
            </div>
          </div>
        </div>
      );
    }

    // 기본 완료 메시지 (completed)
    return (
      <div className='flex-center flex-col gap-4'>
        {!visit?.archived && (
          <>
            <div className='pt-12 pb-6 w-1/3'>
              <img src={thumbsUp} />
            </div>

            <div className='flex-col gap-2 flex-center-y'>
              <h4 className='font-bold sm'>Thank You!</h4>
              <div className='text-base opacity-70 px-2 text-center'>
                Your information has been sent to your care team because your visit has started.
              </div>
            </div>
          </>
        )}

        {visit?.archived && (
          <div className='flex-center flex-col gap-4 py-10'>
            <div className='flex-col gap-2 flex-center-y'>
              <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
                {sltStr({ key: 'finished-archived' })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
      <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={container.logoUrl} className='h-6 w-auto' />
          </div>

          {renderMessage()}

          {survey.access_restriction && survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly &&
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button variant="contained"
                  onClick={() => {
                    setAllowNavigate(true);
                    nav(`/sp/${visit?.uuid}`);
                  }}
                >View Medical Provider Report</Button>
              </div>
            </div>

          }

          {/* FAQ */}
          {notEmpty(metaItems) && (
            <div className='flex-col py-4 w-full'>
              {metaItems?.map((metaItem, idx) => {
                const open = selectedFaqIndex === idx;
                return (
                  <div
                    key={metaItem.id}
                    className={classNames(collapCls, open && 'collapse-open')}
                    onClick={() => {
                      if (open) {
                        setSelectedFaqIndex(null);
                      } else {
                        setSelectedFaqIndex(idx);
                      }
                    }}
                  >
                    <div className={collapHeaderCls}>{t(metaItem.qnaQuestionKey, tOptions)}</div>
                    <div className={collapBodyCls}>
                      <p>{t(metaItem.qnaAnswerKey, tOptions)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
    </div>
  );
}

function PaSurveyTerminatedPage() {
  return (
    <PatientOrgContainer.Provider>
      <PaSurveyTerminatedPageContent />
    </PatientOrgContainer.Provider>
  );
}

export default memo(PaSurveyTerminatedPage);
